import React, {useState} from 'react';
import styled from 'styled-components';
import SGBody from 'components/Typography/SGBody';
import DeviceType from 'features/POSRegisters/interfaces/DeviceType';
import ScanDetails, {
  PaymentTerminalScanDetails,
} from 'features/POSRegisters/interfaces/ScanDetails';
import ModalIconTitle from 'features/POSRegisters/components/ModalContent/components/ModalTitle';
import ModalMainContentContainer from './components/ModalContentContainer';
import DataRow, {DataColumn} from 'features/POSRegisters/POSDiagram/components/DataGrid/DataRow';
import ModalTabs, {ModalTab} from './components/ModalTabs';
import SeverityEnum, {SeverityPriorityEnum} from 'features/POSRegisters/interfaces/Severity';
import ModalSubHeader from './components/ModalSubHeader';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import TabPanel from 'components/Tabs/TabPanel';
import getSubTypeAlerts from 'features/POSRegisters/functions/getSubTypeAlerts';
import {AlertScanSubType} from 'features/POSRegisters/interfaces/AlertInfo';
import {AdaptedDeviceData} from 'features/POSRegisters/interfaces/AdaptedDeviceData';

//#region Styled Components
const StyledModalContent = styled.div``;
//#endregion Styled Components

//#region Column Definitions
const connectivityColumns: DataColumn[] = [
  {
    id: 'pingStatus',
    label: 'PING STATUS',
    width: '6rem',
    format: ({pingStatus}) => <SGBody variant={'b4'}>{pingStatus ?? 'Failure'}</SGBody>,
  },
  {
    id: 'ipAddress',
    label: 'IP Address',
    width: 'auto',
    minWidth: '130px',
  },
  {
    id: 'macAddress',
    label: 'MAC Address',
    width: 'auto',
    minWidth: '160px',
  },
  {
    id: 'vlanNumber',
    label: 'VLAN',
    width: 'auto',
    minWidth: '80px',
  },
  {
    id: 'switchId',
    label: 'Switch ID',
    width: 'auto',
    minWidth: '100px',
  },
  {
    id: 'switchNumber',
    label: 'Switch Number',
    width: 'auto',
    minWidth: '100px',
  },
  {
    id: 'portNumber',
    label: 'Port Number',
    width: 'auto',
    minWidth: '100px',
  },
];

const informationHardwareColumns: DataColumn[] = [
  {
    id: 'deviceName',
    label: 'DEVICE NAME',
    width: 'auto',
    minWidth: '10rem',
  },
  {
    id: 'manufacturer',
    label: 'Manufacturer',
    width: 'auto',
    minWidth: '160px',
  },
  {
    id: 'model',
    label: 'Model',
    width: 'auto',
    minWidth: '120px',
  },
  {
    id: 'serialNumber',
    label: 'Serial Number',
    width: 'auto',
    minWidth: '400px',
  },
];

const apiColumns: DataColumn[] = [
  {
    id: 'isAssociated',
    label: 'Is Associated',
    width: 'auto',
    minWidth: '120px',
  },
  {
    id: 'serialNumber',
    label: 'Serial Number',
    width: 'auto',
    minWidth: '200px',
  },
  {
    id: 'ipAddress',
    label: 'IP Address',
    width: 'auto',
    minWidth: '160px',
  },
];

//#endregion Column Definitions

//#region Component
interface ModalContent {
  deviceType: DeviceType;
  adaptedDeviceData: AdaptedDeviceData<PaymentTerminalScanDetails> | null;
}

export default function ReceiptPrinterModalContent({deviceType, adaptedDeviceData}: ModalContent) {
  const [activeTab, setActiveTab] = useState<ModalTab>(ModalTab.CONNECTIVITY);

  if (!adaptedDeviceData) {
    return <div>No device data available</div>;
  }
  const paymentTerminal = adaptedDeviceData?.paymentTerminal;
  const scanDetails = paymentTerminal?.scanResults as ScanDetails;
  const alerts = paymentTerminal?.alerts;

  const paymentTerminalSeverity = getPriorityAlert([paymentTerminal]);
  const receiptPrinterhasIssues =
    Number(SeverityPriorityEnum[paymentTerminalSeverity.toUpperCase() as any]) > 1;

  const tabs: {tab: ModalTab; severity?: SeverityEnum; isValid?: boolean}[] = [
    {
      tab: ModalTab.CONNECTIVITY,
      severity: getPriorityAlert([
        {
          scanResults: paymentTerminal.scanResults,
          alerts: getSubTypeAlerts(alerts, AlertScanSubType.CONNECTIVITY),
        },
      ]),
      isValid: scanDetails !== undefined,
    },
    {
      tab: ModalTab.INFORMATION,
      severity: getPriorityAlert([
        {
          scanResults: paymentTerminal.scanResults,
          alerts: getSubTypeAlerts(alerts, AlertScanSubType.HARDWARE),
        },
      ]),
      isValid: scanDetails !== undefined,
    },
    // {tab: ModalTab.API, isValid: scanDetails !== undefined},
  ];

  const handleTabChange = (tab: ModalTab) => {
    setActiveTab(tab);
  };

  return (
    <StyledModalContent>
      <ModalIconTitle
        deviceType={deviceType}
        severity={receiptPrinterhasIssues ? paymentTerminalSeverity : undefined}
        deviceName={adaptedDeviceData?.id}
      >
        <ModalTabs handleTabChange={handleTabChange} activeTabs={tabs} selectedTab={activeTab} />
      </ModalIconTitle>

      <ModalMainContentContainer>
        <TabPanel value={activeTab} index={ModalTab.CONNECTIVITY}>
          <ModalSubHeader>CONNECTIVITY</ModalSubHeader>
          <DataRow
            columns={connectivityColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.CONNECTIVITY)}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={ModalTab.INFORMATION}>
          <ModalSubHeader>HARDWARE</ModalSubHeader>
          <DataRow
            columns={informationHardwareColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.HARDWARE)}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={ModalTab.API}>
          <ModalSubHeader>TERMINAL API</ModalSubHeader>
          <DataRow columns={apiColumns} data={scanDetails} />
        </TabPanel>
      </ModalMainContentContainer>
    </StyledModalContent>
  );
}
//#endregion Component
